import { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../../components/BodyCopy";
import {
  Button,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { AuraLayout } from "../../../components/AuraLayout";
import { ErrorAlertIcon as ErrorIcon } from "../../../assets/icons";
import { FlowHeroMark } from "../../../components/FlowHeroMark";
import { useStore } from "../../../store/globalState";
import { usePartnerStore } from "../../../store/usePartnerStore";
import { getEvents, safeCapture } from "../../../analytics";
import { ScrollFadeIndicator } from "../../../components/ScrollFadeIndicator";

/**
 * Shown when the user's session expired (link expired or not found). Offers a
 * return-to-app button only when the partner configured an http(s) URL.
 */
export const SessionExpiredPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const callbackUrl = usePartnerStore((s) => s.sessionExpiredRedirectUrl);
  const canExitToPartner =
    !!callbackUrl &&
    (callbackUrl.startsWith("http://") || callbackUrl.startsWith("https://"));

  const colorConfig = theme.errorView;
  const { backgroundBtn, textColorBtn, title, bodyCopy, iconColors, backgroundIcon } =
    colorConfig;

  useEffect(() => {
    safeCapture(getEvents().SESSION_EXPIRED);
  }, []);

  const handleReturnToApp = () => {
    if (!canExitToPartner || !callbackUrl) return;
    safeCapture(getEvents().EXIT_CLICK, { screen: "session_expired" });
    window.location.href = callbackUrl;
  };

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark backgroundIcon={backgroundIcon}>
            <ErrorIcon size={60} colors={iconColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("error.sessionExpired")}
              highlightColor={title}
              secudnary=""
              secudnaryColor={title}
            />
            <BodyCopy textColor={bodyCopy}>
              {t("error.sessionExpiredMessage")}
            </BodyCopy>
          </section>
        </section>
      </Layout.Content>
      {canExitToPartner && (
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="small"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={handleReturnToApp}
            >
              {t("error.returnToApp")}
            </Button>
          </section>
        </Layout.Footer>
      )}
    </AuraLayout>
  );
});
